import { useEffect, useRef, useState } from "react";
import { CheckCircle2, XCircle, AlertTriangle, Info, HelpCircle } from "lucide-react";
import { __registerModalHandler } from "@/lib/modal";

const STYLES = {
  success: { Icon: CheckCircle2,  ring: "#10B981", bg: "rgba(16,185,129,0.12)",  fg: "#047857", title: "Tudo certo!" },
  error:   { Icon: XCircle,       ring: "#F43F5E", bg: "rgba(244,63,94,0.12)",   fg: "#9F1239", title: "Ops, algo deu errado" },
  warning: { Icon: AlertTriangle, ring: "#F59E0B", bg: "rgba(245,158,11,0.14)",  fg: "#92400E", title: "Atenção" },
  info:    { Icon: Info,          ring: "#1660D6", bg: "rgba(22,96,214,0.10)",   fg: "#0A2A73", title: "Aviso" },
  confirm: { Icon: HelpCircle,    ring: "#0A2A73", bg: "rgba(10,42,115,0.08)",   fg: "#0A2A73", title: "Confirmar ação" },
};

/**
 * Renderizador único do modal global. Montar uma vez na raiz (App.js).
 * Lê as chamadas de `modal.*` (lib/modal.js), enfileira e exibe uma de cada vez.
 */
export default function ModalHost() {
  const [current, setCurrent] = useState(null);
  const queueRef = useRef([]);
  const showingRef = useRef(false);
  const primaryRef = useRef(null);

  const flush = () => {
    if (showingRef.current) return;
    const next = queueRef.current.shift();
    if (next) {
      showingRef.current = true;
      setCurrent(next);
    }
  };

  useEffect(() => {
    return __registerModalHandler((item) => {
      queueRef.current.push(item);
      flush();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = (cur, result) => {
    if (cur) cur.resolve(result);
    showingRef.current = false;
    setCurrent(null);
    setTimeout(() => {
      flush();
      // Defesa anti-congelamento: um <Dialog>/<AlertDialog> Radix deixa `pointer-events:none`
      // no <body>. Se ele já fechou e não há mais nenhum dialog aberto, destrava o body
      // (senão a página/o próximo modal podem ficar inclicáveis).
      try {
        if (document.body.style.pointerEvents === "none" &&
            !document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) {
          document.body.style.pointerEvents = "";
        }
      } catch (_) { /* noop */ }
    }, 0);
  };

  // Teclado: Enter confirma/ok, Esc cancela (confirm) ou fecha (alert)
  useEffect(() => {
    if (!current) return;
    const isConfirm = current.payload.type === "confirm";
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); finish(current, isConfirm ? false : true); }
      else if (e.key === "Enter") { e.preventDefault(); finish(current, true); }
    };
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => { try { primaryRef.current?.focus(); } catch (_) {} }, 60);
    return () => { window.removeEventListener("keydown", onKey); clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  if (!current) return null;

  const p = current.payload;
  const isConfirm = p.type === "confirm";
  const st = STYLES[p.type] || STYLES.info;
  const Icon = st.Icon;
  const title = p.title || st.title;
  const danger = p.tone === "danger";
  const details = Array.isArray(p.details) ? p.details : null;

  const confirmText = p.confirmText || (isConfirm ? "Confirmar" : "Entendi");
  const cancelText = p.cancelText || "Cancelar";

  return (
    <div className="modal-overlay"
         role="presentation"
         onMouseDown={(e) => { if (e.target === e.currentTarget) finish(current, isConfirm ? false : true); }}>
      <div className="modal-card" role="alertdialog" aria-modal="true" aria-label={title}>
        <div className="modal-badge" style={{ background: st.bg, color: st.fg, boxShadow: `0 0 0 6px ${st.bg}` }}>
          <Icon size={26} strokeWidth={2.2} />
        </div>

        <h3 className="modal-title font-serif-display">{title}</h3>

        {p.message ? <p className="modal-message">{p.message}</p> : null}

        {details && details.length > 0 && (
          <div className="modal-details">
            {details.map((d, i) =>
              d && typeof d === "object" ? (
                <div key={i} className="modal-detail-row">
                  <span className="modal-detail-label">{d.label}</span>
                  <span className="modal-detail-value">{d.value}</span>
                </div>
              ) : (
                <div key={i} className="modal-detail-row modal-detail-row--plain">
                  <span className="modal-detail-dot" style={{ background: st.ring }} />
                  <span>{d}</span>
                </div>
              )
            )}
          </div>
        )}

        <div className="modal-actions">
          {isConfirm && (
            <button type="button" className="btn-ghost modal-btn" data-testid="modal-cancel"
                    onClick={() => finish(current, false)}>
              {cancelText}
            </button>
          )}
          <button type="button" ref={primaryRef} data-testid="modal-ok"
                  className={`btn-action modal-btn modal-btn--primary ${danger ? "modal-btn--danger" : "btn-salvar"}`}
                  onClick={() => finish(current, isConfirm ? true : true)}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
